import { ConnectedOverlayPositionChange, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { find, isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { NimbusTooltipService } from '@nimbus-library/features/shared/services/tooltip/nimbusTooltip.service';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';
import { DutyPresence, DutyState, UserState } from '@nimbus-library/features/user-state/models/user-state';
import { UserTaskStateService } from '@nimbus-library/features/user-state/services/user-task-state.service';
import { IParticipantPresence } from '@nimbus-global/features/ui/models/i-participant-presence.model';
import { PresenceActivityTranslateMap } from '@nimbus-global/features/ui/models/presence-activity.mapper';
import { PresenceStatusTranslateMap } from '@nimbus-global/features/ui/models/presence-status.mapper';
import { PresenceLookupService } from '@nimbus-global/features/ui/services/presence-lookup.service';
import { IUserResponsibilityProfile } from '../models/agent-assistant.model';
import { AgentAssistantService } from '../services/agent-assistant.service';

@Component({
  standalone: false,
  selector: 'nimbus-agent-assistant',
  templateUrl: './agent-assistant.component.html',
  styleUrls: ['./agent-assistant.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentAssistantComponent implements OnInit, OnDestroy {
  @ViewChild('origin', { read: ElementRef }) private originRef: ElementRef;
  private readonly subscription$ = new Subscription();

  public ICONS = ICONS;
  public TranslationKeys = TranslationKeys;
  public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public cdkArrowCssClass$: BehaviorSubject<string> = new BehaviorSubject(this.nimbusTooltipService.getCdkArrowCssClass());
  public cdkConnectedPositions: ConnectedPosition[] = this.nimbusTooltipService.getCdkConnectedPositions(['right-bottom']);

  public userResponsibilityProfiles$: BehaviorSubject<IUserResponsibilityProfile[]> = new BehaviorSubject<IUserResponsibilityProfile[]>([]);
  public isOffDuty$: Observable<boolean>;
  public presenceClass$: Observable<string>;
  public dutyImageSrc$: Observable<string>;
  public presence$: Observable<IParticipantPresence | null>;
  public presenceTooltip$: Observable<string>;

  public constructor(
    private readonly agentAssistantService: AgentAssistantService,
    private readonly nimbusTooltipService: NimbusTooltipService,
    private readonly presenceLookupService: PresenceLookupService,
    private readonly store: Store,
    private readonly userTaskStateService: UserTaskStateService,
  ) {}

  public ngOnInit(): void {
    this.subscription$.add(
      this.agentAssistantService.getUserResponsibilityProfiles$().subscribe((x) => this.userResponsibilityProfiles$.next(x)),
    );
    this.isOffDuty$ = this.userResponsibilityProfiles$.pipe(
      skipWhile((x) => !x),
      map((x) => find(x, (y) => y.active)),
      map((x) => !x || !x.isDuty),
    );
    this.presenceClass$ = combineLatest([
      this.userTaskStateService.getRonaStartedAt$(),
      this.userTaskStateService.getUserState$(),
      this.userTaskStateService.getOnDutyState$(),
    ]).pipe(
      map(([ronaStartedAt, userState, onDutyState]) => {
        if (!!ronaStartedAt) {
          return 'Rona';
        }

        if (userState === UserState.Selectable) {
          return DutyPresence.Available;
        }

        return onDutyState;
      }),
      skipWhile((x) => x === null),
      distinctUntilChanged(),
    );
    this.dutyImageSrc$ = combineLatest([this.store.select(CommonThemeState.theme), this.isOffDuty$.pipe()]).pipe(
      map(([theme, isOffDuty]) => {
        const dutyModifier = isOffDuty ? DutyState.Off : DutyState.On;
        const themeModifier = theme === ChartTheme.Dark ? '-dark' : '';
        return `images/${dutyModifier}-duty${themeModifier}.svg`;
      }),
    );
    this.presence$ = this.userTaskStateService.getUserPresence$();
    this.presenceTooltip$ = this.presence$.pipe(
      map((presence) =>
        this.presenceLookupService.getPresenceTitle(
          presence?.activity,
          presence?.availability,
          PresenceActivityTranslateMap,
          PresenceStatusTranslateMap,
        ),
      ),
      distinctUntilChanged(isEqual),
    );
  }

  public togglePopup() {
    this.subscription$.add(
      this.agentAssistantService.getUserResponsibilityProfiles$().subscribe((x) => this.userResponsibilityProfiles$.next(x)),
    );
    this.isOpen$.next(!this.isOpen$.value);
  }

  public onPositionChange(e: ConnectedOverlayPositionChange) {
    this.cdkArrowCssClass$.next(this.nimbusTooltipService.getCdkArrowCssClass(e));
  }

  public onBackdropClick() {
    this.isOpen$.next(false);
    this.originRef?.nativeElement?.focus();
  }

  public onResponsibilityProfileChanged(responsibilityProfileId: string) {
    this.subscription$.add(
      this.agentAssistantService
        .setUserResponsibilityProfile(responsibilityProfileId)
        .pipe(
          take(1),
          switchMap(() => this.agentAssistantService.getUserResponsibilityProfiles$()),
          tap((x) => this.userResponsibilityProfiles$.next(x)),
        )
        .subscribe(),
    );
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
